import {faAt, faChevronDown, faExternalLink, faPhone} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Divider, Dropdown, Flex, Space, Tooltip} from 'antd';
import {ReactElement, useCallback, useMemo, useState} from 'react';

import {Text} from '../../../../../component/text/text';
import {RatingStarLine} from '../../../../../layout/rating-star-line/rating-star-line';
import {Locale} from '../../../../../provider/locale/localization';
import {useUser} from '../../../../../provider/user/user-hook';
import {formatAddress} from '../../../../../service/address/address-helper';
import {fetchReviewSentimentUpdate} from '../../../../../service/reviews/reviews';
import {useReviewsInfo} from '../../../../../service/reviews/reviews-info';
import {ReviewNoteType} from '../../../../../service/reviews/reviews-notes';
import {ReviewSuggestionType} from '../../../../../service/reviews/reviews-suggestions';
import {useReviewSentiments} from '../../../../../service/reviews/reviews-tags';
import {
    ReviewDataType,
    ReviewSentimentEnum,
    ReviewSentimentOptionType,
    ReviewSentimentType,
} from '../../../../../service/reviews/reviews-type';
import {useCopy} from '../../../../../util/copy-hook/copy-hook';
import {useFormat} from '../../../../../util/format-hook/format-hook';
import {NewReviewAnswers} from '../../review-answers/new-review-answers';
import {ReviewCatalogLogo} from '../../review-catalog-logo/review-catalog-logo';
import {ScrollButton} from '../../review-item-page-content/scroll-button/scroll-button';
import {NewNote} from '../../review-list-page-content/new-review-notes/new-note/new-note';
import {ReviewAdditionalDataTags} from '../../review-list-page-content/review-list/review/review-additional-data/review-additional-data-tags';
import {StatusTag} from '../../review-list-page-content/review-list/review/review-header/status-tag/status-tag';
import {
    constructComment,
    hasProblemSolvedInComment,
    ReviewProblemSolvedBadge,
} from '../../review-list-page-content/review-list/review/review-helper';
import {ReviewDataWithCatalogType} from '../../review-list-page-content/review-list/review-list-type';
import {getReviewCreationDate} from '../../reviews-helper';

import * as styles from './review-wrapper.scss';

type PropsType = {
    review: ReviewDataWithCatalogType;
    isCatalogLoading: boolean;
    refreshSuggestions: () => Promise<unknown>;
    refreshReviewItem: () => Promise<unknown>;
    refreshReview?: (reviewId: number) => Promise<unknown>;
    updateReviewItem: (data: Partial<ReviewDataType>) => void;
    openHistoryModalHandler: () => void;
    suggestion?: ReviewSuggestionType;
};

// eslint-disable-next-line complexity
export function ReviewWrapper(props: PropsType): ReactElement {
    const {
        review,
        suggestion,
        isCatalogLoading,
        refreshSuggestions,
        refreshReviewItem,
        refreshReview,
        updateReviewItem,
        openHistoryModalHandler,
    } = props;

    const {
        author,
        catalogId,
        comment,
        notes,
        id,
        rating,
        userFields,
        createdInCatalog,
        company,
        brand,
        deletedAt,
        originUrl,
        catalog,
        createdInRd,
        isHidden,
        lastChangesDate,
        sentiment,
    } = review;

    const {data: reviewsInfo} = useReviewsInfo([id]);
    const additionalData = reviewsInfo?.results.find((reviewInfo) => reviewInfo.id === id)?.additionalData;

    const {user} = useUser();
    const {data: sentimentsMap} = useReviewSentiments();
    const [isSentimentUpdating, setIsSentimentUpdating] = useState(false);
    const {getFormattedDateTime} = useFormat();

    const onCopy = useCopy('TEXT__COPIED');

    const answerRefreshHandler = useCallback(
        (reviewItemId: number) => {
            if (reviewItemId === id) {
                return refreshReviewItem();
            }

            return Promise.resolve();
        },
        [refreshReviewItem, id]
    );

    const updateNote = useCallback(
        (data: ReviewNoteType) => {
            updateReviewItem({notes: notes?.map((note) => (note.id === data.id ? data : note))});
        },
        [notes, updateReviewItem]
    );

    const deleteNote = useCallback(
        (noteId: number | string) => {
            updateReviewItem({notes: (notes ?? []).filter((note) => note.id !== noteId)});
        },
        [notes, updateReviewItem]
    );

    const timeZoneObject = user?.profile?.timezone ? {timeZone: user?.profile?.timezone} : {};

    const showProblemSolvedBadge = useMemo(() => hasProblemSolvedInComment({comment, catalogId}), [comment, catalogId]);
    const commentElements = useMemo(
        () => (comment ? constructComment({text: comment, withLinks: true, catalogId}) : null),
        [comment, catalogId]
    );
    const sentimentItems = useMemo<Array<ReviewSentimentOptionType>>(
        () =>
            sentimentsMap
                ? [
                      {
                          key: ReviewSentimentEnum.positive,
                          label: sentimentsMap.positive,
                      },
                      {
                          key: ReviewSentimentEnum.neutral,
                          label: sentimentsMap.neutral,
                      },
                      {
                          key: ReviewSentimentEnum.negative,
                          label: sentimentsMap.negative,
                      },
                  ]
                : [],
        [sentimentsMap]
    );

    const currentSentimentLabel = useMemo(() => {
        if (!sentimentsMap || !sentiment) {
            return null;
        }

        return sentimentsMap[sentiment];
    }, [sentimentsMap, sentiment]);

    const handleSentimentChange = useCallback(
        async (newSentiment: ReviewSentimentType) => {
            if (sentiment === newSentiment || isSentimentUpdating) {
                return;
            }

            try {
                setIsSentimentUpdating(true);
                await fetchReviewSentimentUpdate(id, {sentiment: newSentiment});
                updateReviewItem({sentiment: newSentiment as ReviewSentimentEnum});

                if (refreshReview) {
                    await refreshReview(id);
                }
            } finally {
                setIsSentimentUpdating(false);
            }
        },
        [id, isSentimentUpdating, sentiment, updateReviewItem, refreshReview]
    );

    const catalogLine = catalog && (
        <div className={styles.ReviewWrapper_catalogCompanyLine}>
            <div className={styles.ReviewWrapper_catalogLogo}>
                <ReviewCatalogLogo isCatalogLoading={isCatalogLoading} logoUrl={catalog.logo} />
            </div>
            {!deletedAt && originUrl ? (
                <a className={styles.ReviewWrapper_externalLink} href={originUrl} rel="noreferrer" target="_blank">
                    {catalog.name}
                    <FontAwesomeIcon icon={faExternalLink} />
                </a>
            ) : (
                <Text secondary>{catalog.name}</Text>
            )}
            <Text secondary>•</Text>
        </div>
    );

    return (
        <div className={styles.ReviewWrapper_review}>
            <Flex className={styles.ReviewWrapper_infoWrapper} vertical>
                <Flex vertical>
                    <Space size={12} wrap>
                        {author && (
                            <Text bolder large>
                                {author}
                            </Text>
                        )}
                        <Tooltip
                            placement="bottomLeft"
                            rootClassName={styles.ReviewWrapper_parseDateTooltip}
                            title={
                                <Flex vertical>
                                    <Flex className={styles.ReviewWrapper_parseDate}>
                                        <Locale
                                            stringKey="REVIEWS__REVIEWS_PARSED_AT"
                                            valueMap={{
                                                date: (
                                                    <span>
                                                        {getFormattedDateTime(new Date(createdInRd), {
                                                            year: 'numeric',
                                                            month: '2-digit',
                                                            day: '2-digit',
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                            ...timeZoneObject,
                                                        })}
                                                    </span>
                                                ),
                                            }}
                                        />
                                    </Flex>

                                    {createdInCatalog && (
                                        <Flex className={styles.ReviewWrapper_parseDate}>
                                            <Locale
                                                stringKey="REVIEWS__REVIEWS_IN_CATALOG"
                                                valueMap={{
                                                    date: getFormattedDateTime(new Date(createdInCatalog), {
                                                        year: 'numeric',
                                                        month: '2-digit',
                                                        day: '2-digit',
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                        ...timeZoneObject,
                                                    }),
                                                }}
                                            />
                                        </Flex>
                                    )}
                                </Flex>
                            }
                        >
                            <Text secondary>
                                {getFormattedDateTime(new Date(getReviewCreationDate(review)), {
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                    ...timeZoneObject,
                                })}
                            </Text>
                        </Tooltip>
                    </Space>
                    <Space align="center" size={12} wrap>
                        {typeof rating === 'number' && <RatingStarLine rating={rating} />}
                        {sentimentItems.length > 0 && !deletedAt && sentiment !== null && (
                            <Dropdown
                                disabled={isSentimentUpdating}
                                menu={{
                                    items: sentimentItems,
                                    onClick: ({key}) => handleSentimentChange(key as ReviewSentimentType),
                                }}
                                placement="bottomLeft"
                                trigger={['click']}
                            >
                                <Button className={styles.ReviewWrapper_sentimentSelect} size="small" type="text">
                                    <Space size={4}>
                                        <Text>{currentSentimentLabel}</Text>
                                        <FontAwesomeIcon icon={faChevronDown} />
                                    </Space>
                                </Button>
                            </Dropdown>
                        )}
                        <StatusTag
                            isDeleted={Boolean(deletedAt)}
                            isHidden={Boolean(isHidden)}
                            lastChangesDate={lastChangesDate}
                            openHistoryModalHandler={openHistoryModalHandler}
                        />
                    </Space>
                    {(userFields.phone || userFields.email) && (
                        <Space wrap>
                            {userFields?.phone && (
                                <Space
                                    className={styles.ReviewWrapper_userEmail}
                                    onClick={() => onCopy(userFields.phone as string)}
                                    size={4}
                                >
                                    <FontAwesomeIcon icon={faPhone} />
                                    <Text inheritColor>{userFields.phone}</Text>
                                </Space>
                            )}
                            {userFields?.email && (
                                <Space
                                    className={styles.ReviewWrapper_userEmail}
                                    onClick={() => onCopy(userFields.email as string)}
                                    size={4}
                                >
                                    <FontAwesomeIcon icon={faAt} />
                                    <Text inheritColor>{userFields.email}</Text>
                                </Space>
                            )}
                        </Space>
                    )}
                </Flex>
                {!company ? (
                    <Flex gap={4} wrap="wrap">
                        {catalogLine}
                        <Text bolder lighter>
                            {brand?.name}
                        </Text>
                    </Flex>
                ) : (
                    <Flex gap={4} wrap="wrap">
                        {catalogLine}
                        <Text bolder lighter>
                            {company?.name}
                        </Text>
                        <div className={styles.ReviewWrapper_catalogCompanyLine}>
                            <Text lighter>{formatAddress(company?.address || null)}</Text>
                            {company?.code && (
                                <Tooltip
                                    placement="bottom"
                                    title={<Locale stringKey="REVIEWS__REVIEW__COMPANY_CODE__HINT" />}
                                >
                                    <Text secondary>{company.code}</Text>
                                </Tooltip>
                            )}
                        </div>
                    </Flex>
                )}
                <ReviewAdditionalDataTags additionalData={additionalData} />
                {comment ? (
                    <>
                        {showProblemSolvedBadge && <ReviewProblemSolvedBadge />}
                        <div className={styles.ReviewWrapper_commentBody}>{commentElements}</div>
                    </>
                ) : (
                    <div className={styles.ReviewWrapper_noText}>
                        <Text secondary small stringKey="REVIEW_ITEM_PAGE__NO_TEXT_TITLE" />
                    </div>
                )}
                <Flex className={styles.ReviewWrapper_noteContainer} vertical>
                    {notes?.map((note: ReviewNoteType) => {
                        return (
                            <NewNote
                                createdAt={note.createdAt}
                                id={note.id}
                                key={note.id}
                                onNoteDelete={deleteNote}
                                onNoteEdit={(data) => updateNote(data)}
                                reviewId={id}
                                text={note.text}
                                updatedAt={note.updatedAt}
                                user={note.user}
                            />
                        );
                    })}
                </Flex>
                <Divider className={styles.ReviewWrapper_answerDivider} />
                {review && (
                    <NewReviewAnswers
                        isCollapsedAnswers={false}
                        isGptSuggestSupported={catalog?.gpt_suggest_supported}
                        itemPageMode
                        refreshReview={answerRefreshHandler}
                        refreshSuggestions={refreshSuggestions}
                        review={review}
                        suggestion={suggestion}
                    />
                )}
            </Flex>

            <div className={styles.ReviewWrapper_scrollButtonWrapper}>
                <ScrollButton />
            </div>
        </div>
    );
}
